import { throwErrorWithId } from '@pypestream/utils';
import { reportDegradedLoad } from './helpers/report-degraded-load';

import {
  getCustomerPypes,
  resolveBotSolutionId,
  withRoutingConfig,
} from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import { Pype } from '../prostudio.api-service.types';

export const loadBotPypes = async (
  ctx: ProStudioContext,
  _event: ProStudioEvents
): Promise<{ botPypes: Pype[] }> => {
  const { botName, customerId, project, solutions } = ctx;

  if (!project) {
    throwErrorWithId('Project is required');
    return { botPypes: [] };
  }

  if (!botName) {
    throwErrorWithId('Bot Name is required');
    return { botPypes: [] };
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return { botPypes: [] };
  }

  const uniqueCustomerId = project?.projectProStudioSetting?.customerId;

  const projectId = project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return { botPypes: [] };
  }

  if (!uniqueCustomerId) {
    throwErrorWithId('Customer ID is required');
    return { botPypes: [] };
  }

  const botId = resolveBotSolutionId({ solutions, botName });

  if (!botId) {
    throwErrorWithId('Bot ID is required');
    return { botPypes: [] };
  }

  // Backend reads here (pypes-api, admin-backend, v3 config service) are
  // observably unstable in CI/preview environments. A throw here propagates
  // to the calling XState invoke and wipes ctx.botPypes — dependent views
  // (channels.tsx derives its table from botPypes) then hang for 120–180 s
  // waiting for state that will never arrive. Two-layer resilience:
  //   1. Retry with exponential backoff on transient failures.
  //   2. If retries are exhausted AND we have a previous good snapshot,
  //      degrade gracefully (preserve last-known data). On cold start
  //      (no previous data) rethrow so the state machine routes to '#error',
  //      which has a recovery path — silently returning [] would render an
  //      "empty channels" UI that is indistinguishable from a real empty.
  type CustomerPypesResult = Awaited<ReturnType<typeof getCustomerPypes>>;
  const RETRY_DELAYS_MS = [500, 1500, 4000];
  let livePypes: CustomerPypesResult['live'] = [];
  let sandboxPypes: CustomerPypesResult['sandbox'] = [];
  let lastErr: unknown;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      ({ live: livePypes, sandbox: sandboxPypes } = await getCustomerPypes({
        uniqueCustomerId,
        projectId,
        botId,
      }));
      lastErr = undefined;
      break;
    } catch (err) {
      lastErr = err;
      const delay = RETRY_DELAYS_MS[attempt];
      if (delay !== undefined) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  if (lastErr) {
    const hasPreviousSnapshot = (ctx.botPypes?.length ?? 0) > 0;
    if (hasPreviousSnapshot) {
      // Preserve previous data even for post-mutation refetches: throwing
      // here would hand `'#botFlowError'` while ctx.botPypes is still set,
      // leading to a recovery loop that transiently hides the table. Stale
      // data showing the pre-mutation server state is a worse-but-usable
      // outcome compared to the table disappearing. Tradeoff is logged.
      console.warn(
        '[loadBotPypes] getCustomerPypes failed after retries; preserving previous botPypes',
        lastErr
      );
      // Reported even though the invoke RESOLVES, which is exactly why it has
      // to be reported here: the shared reporter fires on rejection, and this
      // path deliberately does not reject. The user is shown a channel list
      // that looks current and is not — no toast, no error state — so without
      // this the degradation is invisible on both sides of the screen.
      reportDegradedLoad(lastErr, 'load-bot-pypes', {
        botId,
        projectId,
        preservedPypeCount: ctx.botPypes?.length ?? 0,
      });

      return { botPypes: ctx.botPypes ?? [] };
    }
    // Cold-start failure — surface to XState so '#error' recovery can fire.
    throw lastErr;
  }

  const botPypes = [...livePypes, ...sandboxPypes];

  // `agent_routing_config` and `bot_retry` now come back with the V6 pype list
  // itself, so the routing config is projected locally. This replaces one v3
  // `loadPypeConfig` request per pype — which, besides being N+1, could partially
  // fail and leave some channels showing an empty integration.
  return {
    botPypes: botPypes.map(withRoutingConfig),
  };
};
